// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { usePeriodPicker } from '@shared/advisory/aggregation/hooks';
import { useAccountsPositions } from '@shared/advisory/aggregation/hooks/use-accounts-positions';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api';
import { proposalsApi } from '@shared/data-access/api/brokerage/proposals-api';
import {
  type InternalProposalOperation,
  type InternalTargetPortfolioPosition,
} from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import { selectClientProductsAccounts } from '@shared/data-access/slices/clients';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { createDataProvider, HistValues } from '@shared/ui-kit';
import { isUndefined } from '@shared/utils';
import produce from 'immer';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export interface UseTraderPortfolioProps {
  clientId: number | null;
  operations: InternalProposalOperation[];
}

export const useTraderPortfolio = ({
  clientId,
  operations,
}: UseTraderPortfolioProps) => {
  const periodPicker = usePeriodPicker(HistValues['3Y']);
  const productAccounts = useSelector(selectClientProductsAccounts);
  const currency = useSelector(selectAccountCurrency);

  const { data: profileData } = advisoryApi.useGetClientProfileQuery(
    { client_id: clientId! },
    { skip: !clientId },
  );

  const advisoryProposal = profileData?.advisory_proposal;

  const advisoryProposalAccounts = useMemo(
    () =>
      advisoryProposal?.institutions.flatMap(({ products, ...institution }) => {
        const advisoryInstitution = productAccounts.find(
          ({ institution_id }) => institution_id === institution.institution_id,
        )!;

        if (isUndefined(advisoryInstitution)) return [];

        return produce(advisoryInstitution, draft => {
          draft.accounts = draft.accounts.filter(
            ({ iban }) =>
              !!products.accounts.find(account => account.iban === iban),
          );
        });
      }) ?? [],
    [advisoryProposal?.institutions, productAccounts],
  );

  const unsharedProposalAccounts = useMemo(() => {
    const sharedIbans = new Set(
      productAccounts.flatMap(({ accounts }) => accounts.map(({ iban }) => iban)),
    );

    return (
      advisoryProposal?.institutions.flatMap(institution =>
        institution.products.accounts
          .filter(account => !sharedIbans.has(account.iban))
          .map(account => ({
            iban: account.iban,
            name: account.name ?? account.iban,
            institutionName: institution.name,
          })),
      ) ?? []
    );
  }, [advisoryProposal?.institutions, productAccounts]);

  const ibans = useMemo(
    () =>
      advisoryProposalAccounts.flatMap(({ accounts }) =>
        accounts.map(({ iban }) => iban),
      ),
    [advisoryProposalAccounts],
  );

  const { accountsPositionsQuery, ...accountsPositions } = useAccountsPositions({
    ibans,
    clientId: clientId,
    skip: !clientId || operations.length === 0 || isUndefined(advisoryProposal),
    institutionId: 'all',
    currency,
  });

  const currentPortfolio = useMemo<InternalTargetPortfolioPosition[]>(() => {
    return accountsPositions.holdings
      .map(holding => ({
        iban: holding.iban!,
        ticker: holding?.details?.ticker ?? holding.ticker! ?? holding.isin ?? '',
        isin: holding?.details?.isin ?? holding.isin ?? '',
        quantity: holding.quantity,
        amount: holding.investment,
        institution_id: holding.institution_id!,
      }))
      .concat(
        advisoryProposalAccounts.flatMap(({ accounts, institution_id }) =>
          accounts
            .filter(({ cash }) => cash !== null && cash !== 0)
            .map(account => ({
              ticker: '$CASH',
              iban: account.iban,
              quantity: 1,
              amount: account.cash ?? 0,
              institution_id: institution_id,
              isin: '$CASH',
            })),
        ),
      );
  }, [accountsPositions.holdings, advisoryProposalAccounts]);

  const { data: simulation, ...targetPortfolioQuery } =
    proposalsApi.useCreateTargetPortfolioQuery(
      {
        current_portfolio: currentPortfolio,
        operations: operations,
      },
      {
        skip:
          !clientId ||
          operations.length === 0 ||
          accountsPositionsQuery.isLoading ||
          !accountsPositionsQuery.isSuccess ||
          isUndefined(advisoryProposal),
      },
    );

  const targetPortfolio = simulation?.positions ?? [];

  const benchmark = useMemo(() => {
    const investment = accountsPositions.holdings.reduce(
      (acc, { investment }) => investment + acc,
      0,
    );

    const cash = currentPortfolio
      .filter(({ ticker }) => ticker === '$CASH')
      .reduce((acc, { amount }) => amount + acc, 0);

    const totalInvestment = investment + cash;

    if (totalInvestment <= 0) {
      return [{ ticker: '$CASH', weight: 1 }];
    }

    const data = accountsPositions.holdings.map(holding => ({
      ticker:
        holding.details?.ticker ??
        holding.details?.isin ??
        holding.ticker ??
        holding.isin ??
        '',
      weight: holding.investment / totalInvestment,
    }));

    if (cash !== 0) {
      data.push({
        ticker: '$CASH',
        weight: cash / totalInvestment,
      });
    }

    return data.length > 0 ? data : [{ ticker: '$CASH', weight: 1 }];
  }, [accountsPositions.holdings, currentPortfolio]);

  const targetHoldings = useMemo(() => {
    return targetPortfolio.map(target => ({
      ticker: target.ticker,
      unrealized_capital_gains: 0,
      price: target.price,
      qty: target.quantity,
    }));
  }, [targetPortfolio]);

  const isLoading = useMemo(() => {
    return [
      accountsPositionsQuery.isLoading,
      accountsPositionsQuery.isFetching,
      targetPortfolioQuery.isLoading,
      targetPortfolioQuery.isFetching,
    ].some(Boolean);
  }, [
    accountsPositionsQuery.isLoading,
    accountsPositionsQuery.isFetching,
    targetPortfolioQuery.isLoading,
    targetPortfolioQuery.isFetching,
  ]);

  return {
    currentPortfolio,
    targetPortfolio,
    simulationStatus: simulation?.status,
    operationResults: simulation?.operation_results ?? [],
    unsharedProposalAccounts,
    periodPicker,
    accountHoldings: accountsPositions.holdings,
    benchmark,
    targetHoldings,
    isLoading,
  };
};

export const { TraderPortfolioProvider, useTraderPortfolioData } =
  createDataProvider({
    factory: useTraderPortfolio,
    name: 'TraderPortfolio',
  });
